// =============================================================================
// features/meu-whatsapp/WhatsAppConnectionGuard.tsx — Aviso de WhatsApp
// desconectado para o agente obrigado (F57-S02), montado no AppLayout.
//
// Duas formas do mesmo aviso:
//   - Modal (aqui): abre ao entrar no sistema e a cada NOVA queda; "Depois"
//     dispensa só esta queda nesta aba (connectionGuard.ts).
//   - Selo na topbar (WhatsAppStatusPill): fica enquanto durar a queda e
//     reabre este modal. Vive dentro da topbar fixa — não cobre o campo de
//     digitar e não muda a altura das telas de `calc(100vh - 3.5rem)`.
//
// Conectou: dentro do modal, a tela de sucesso do ConnectDialog; com o modal
// fechado, um toast confirma e o selo some sozinho.
//
// Quem não é obrigado (ex.: gestora com papel de agente) não vê nada: a API
// devolve `required: false` e a regra pura recusa.
// =============================================================================

import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useToast } from '../../components/ui/Toast';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAuthStore } from '../../lib/auth-store';
import { usePersonalEmailGuard } from '../account/usePersonalEmailGuard';

import {
  CONNECT_STEPS,
  REASON_LABEL,
  formatSince,
  hasDismissedGuard,
  hasReconnected,
  markGuardDismissed,
  shouldOpenGuardModal,
  type WhatsAppConnection,
} from './connectionGuard';
import {
  CONNECT_PROMPT_FLAG,
  CONNECT_PROMPT_PERMISSION,
  isConnectPromptRouteExcluded,
} from './connectPrompt';
import { ConnectDialog } from './ConnectWhatsAppPrompt';
import { useSessionStatusRealtime } from './useSessionStatusRealtime';
import {
  useConnectionGuardEnabled,
  useConnectionGuardStore,
  useWhatsAppConnection,
} from './useWhatsAppConnection';

function GuardIntro({
  connection,
  canPair,
}: {
  readonly connection: WhatsAppConnection;
  readonly canPair: boolean;
}): React.JSX.Element {
  const since = formatSince(connection.since);
  const reason = connection.reason !== null ? REASON_LABEL[connection.reason] : null;
  const failed = connection.status === 'FAILED' || connection.status === 'STOPPED';

  return (
    <div className="flex flex-col gap-4">
      <p className="font-sans text-sm font-medium text-ink">
        As mensagens dos seus clientes não estão chegando ao sistema.
      </p>

      {reason !== null ? (
        <div
          className="flex items-start gap-2.5 rounded-sm px-3 py-2.5"
          style={{
            background: failed ? 'var(--danger-bg)' : 'var(--warning-bg)',
            borderLeft: `3px solid ${failed ? 'var(--danger)' : 'var(--warning)'}`,
            boxShadow: 'var(--elev-1)',
          }}
        >
          <span
            aria-hidden="true"
            className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: failed ? 'var(--danger)' : 'var(--warning)' }}
          />
          <p
            className="font-sans text-sm font-semibold"
            style={{ color: failed ? 'var(--danger)' : 'var(--warning)' }}
          >
            {reason}
            {since !== null ? (
              <span className="font-mono font-normal" style={{ fontSize: 'var(--text-xs)' }}>
                {' '}
                · {since}
              </span>
            ) : null}
          </p>
        </div>
      ) : null}

      {canPair ? (
        <ol className="flex flex-col gap-2.5">
          {CONNECT_STEPS.map((step, index) => (
            <li key={step} className="flex gap-3">
              <span
                aria-hidden="true"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono font-semibold text-azul"
                style={{
                  fontSize: 'var(--text-xs)',
                  background: 'var(--info-bg)',
                  boxShadow: 'var(--elev-1)',
                }}
              >
                {index + 1}
              </span>
              <span className="font-sans text-sm text-ink-2 pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="font-sans text-sm text-ink-2">
          Seu usuário ainda não pode conectar o WhatsApp por aqui. Avise seu gestor para liberar a
          conexão.
        </p>
      )}
    </div>
  );
}

function GuardController(): React.JSX.Element | null {
  const { pathname } = useLocation();
  const { toast } = useToast();
  const query = useWhatsAppConnection(true);
  const connection = query.data ?? null;

  // Um único assinante do socket enquanto a guarda está montada: a queda ou a
  // reconexão empurrada pelo backend invalida ['meu-whatsapp', ...], que
  // inclui a consulta desta guarda. (O ConnectDialog monta o seu enquanto
  // aberto; invalidar duas vezes a mesma chave é idempotente.)
  useSessionStatusRealtime();

  const personalEmail = usePersonalEmailGuard();
  const personalEmailPending = personalEmail.isLoading || personalEmail.requiresPersonalEmail;

  const { enabled: pairingFlag } = useFeatureFlag(CONNECT_PROMPT_FLAG);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canPair = pairingFlag && hasPermission(CONNECT_PROMPT_PERMISSION);

  const modalOpen = useConnectionGuardStore((s) => s.modalOpen);
  const openModal = useConnectionGuardStore((s) => s.openModal);
  const closeModal = useConnectionGuardStore((s) => s.closeModal);

  // Espelho em memória do "Depois" (vale se o sessionStorage falhar).
  const [dismissedKeys, setDismissedKeys] = React.useState<ReadonlySet<string>>(new Set());
  const agentId = connection?.agent_id ?? null;
  const since = connection?.since ?? null;
  const dropKey = `${agentId ?? ''}:${since ?? 'never'}`;
  const dismissed =
    dismissedKeys.has(dropKey) || (agentId !== null && hasDismissedGuard(agentId, since));

  const shouldOpen = shouldOpenGuardModal({
    flagEnabled: true,
    connection,
    pathname,
    dismissed,
    personalEmailPending,
  });

  const routeExcluded = isConnectPromptRouteExcluded(pathname);
  React.useEffect(() => {
    if (shouldOpen && !modalOpen) openModal();
    else if (modalOpen && routeExcluded) closeModal(); // o QR desmonta junto
  }, [shouldOpen, modalOpen, routeExcluded, openModal, closeModal]);

  // Confirmação quando reconecta com o modal fechado (o modal tem a própria).
  const previousNeedsAction = React.useRef(false);
  React.useEffect(() => {
    if (connection === null) return;
    if (!modalOpen && hasReconnected(previousNeedsAction.current, connection)) {
      toast('WhatsApp conectado. As mensagens dos seus clientes voltaram a chegar.', 'success');
    }
    previousNeedsAction.current = connection.needs_action;
  }, [connection, modalOpen, toast]);

  const dismiss = React.useCallback(() => {
    setDismissedKeys((prev) => new Set(prev).add(dropKey));
    if (agentId !== null) markGuardDismissed(agentId, since);
    closeModal();
  }, [agentId, since, dropKey, closeModal]);

  if (!modalOpen || connection === null || routeExcluded) return null;

  return (
    <ConnectDialog
      onDismiss={dismiss}
      onFinish={closeModal}
      canPair={canPair}
      intro={{
        title: 'Seu WhatsApp está desconectado',
        body: <GuardIntro connection={connection} canPair={canPair} />,
      }}
    />
  );
}

/**
 * Guarda montada no AppLayout. Sem a flag não monta nada — nem consulta.
 * Com a flag, substitui o convite do F56-S03 (dois modais não podem coexistir).
 */
export function WhatsAppConnectionGuard(): React.JSX.Element | null {
  const enabled = useConnectionGuardEnabled();
  return enabled ? <GuardController /> : null;
}
